import type { AnalyticsTracker } from './AnalyticsTracker';
import { CompositeAnalyticsTracker } from './CompositeAnalyticsTracker';
import { createCrmV2Tracker } from './CrmV2AnalyticsTracker';
import { getCrmV2ProjectToken, getCrmV2SupabaseUrl, isCrmV2Configured } from './crmV2Config';
import { MixpanelProvider, PopowCrmProvider } from './providers';

let trackerInstance: CompositeAnalyticsTracker | null = null;
let crmV2InitPromise: Promise<void> | null = null;

/**
 * Get or create the global analytics tracker instance
 *
 * 🎯 Phase 4: 새 providers 사용 (Super Properties 지원)
 * - MixpanelProvider: Super Properties 자동 등록
 * - PopowCrmProvider: Mixpanel distinctId 연동
 */
export function getAnalyticsTracker(): AnalyticsTracker {
  if (trackerInstance) {
    return trackerInstance;
  }

  // 🎯 새 providers 사용
  const mixpanelProvider = new MixpanelProvider();
  const popowCrmProvider = new PopowCrmProvider();

  // PopowCrmProvider에 MixpanelProvider 연결 (distinctId 공유)
  popowCrmProvider.setMixpanelProvider(mixpanelProvider);

  trackerInstance = new CompositeAnalyticsTracker([mixpanelProvider, popowCrmProvider]);

  // 🎯 CRM v2 tracker 비동기 초기화 시작 (백그라운드)
  if (isCrmV2Configured() && !crmV2InitPromise) {
    crmV2InitPromise = initCrmV2Tracker();
  }

  return trackerInstance;
}

/**
 * CRM v2 tracker를 비동기로 초기화하고 CompositeTracker에 추가
 */
async function initCrmV2Tracker(): Promise<void> {
  try {
    const token = await getCrmV2ProjectToken();
    if (!token) {
      return;
    }

    const supabaseUrl = getCrmV2SupabaseUrl();
    if (!supabaseUrl) {
      return;
    }

    const crmV2Tracker = createCrmV2Tracker(supabaseUrl, token);

    if (trackerInstance) {
      trackerInstance.addTracker(crmV2Tracker);
    }
  } catch {
    // CRM v2 초기화 실패 - 무시 (Mixpanel, Popow CRM은 정상 동작)
  }
}

// ============================================================================
// 🎯 Public API: Analytics 싱글톤
// ============================================================================

/**
 * Analytics - 통합 분석 트래커 싱글톤
 *
 * @example
 * import { Analytics } from '@/features/common/analytics';
 *
 * // 이벤트 트래킹
 * Analytics.track('View Mission Detail', { mission_id: '123' });
 *
 * // 사용자 식별
 * Analytics.identify(userId);
 *
 * // 사용자 속성 설정
 * Analytics.setUserProperties({ plan: 'premium' });
 */
export const Analytics = {
  /**
   * 이벤트 트래킹
   * @param eventName - 이벤트명 ('{Verb} {Noun}' 형식, 예: 'View Mission Detail')
   * @param properties - 이벤트 속성 (snake_case, 예: { mission_id: '123' })
   */
  track: (eventName: string, properties?: Record<string, unknown>): void => {
    getAnalyticsTracker().track(eventName, properties);
  },

  /**
   * 사용자 식별
   */
  identify: async (userId: string): Promise<void> => {
    const tracker = getAnalyticsTracker();
    if (tracker.identify) {
      await tracker.identify(userId);
    }
  },

  /**
   * 사용자 속성 설정
   */
  setUserProperties: async (properties: Record<string, unknown>): Promise<void> => {
    const tracker = getAnalyticsTracker();
    if (tracker.setUserProperties) {
      await tracker.setUserProperties(properties);
    }
  },

  /**
   * Distinct ID 조회
   */
  getDistinctId: async (): Promise<string | null> => {
    const tracker = getAnalyticsTracker();
    if (tracker.getDistinctId) {
      return tracker.getDistinctId();
    }
    return null;
  },
};
